import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const PrintOrderCommon = ({ orderArray, firmArray, items }) => {
  // Get data from sessionStorage if props are not provided
  const [sessionData, setSessionData] = useState(null);
  
  useEffect(() => {
    // Check if we need to get data from sessionStorage
    if (!orderArray?.length && !sessionData) {
      try {
        const storedData = sessionStorage.getItem('invoiceData');
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setSessionData(parsedData);
          // Clear the session storage to avoid data persisting
          sessionStorage.removeItem('invoiceData');
        }
      } catch (error) {
        console.error('Error retrieving data from sessionStorage:', error);
      }
    }
  }, [orderArray, sessionData]);

  // Use props or session data
  const effectiveOrderArray = orderArray?.length ? orderArray : sessionData?.invoiceArray || [];
  const effectiveFirmArray = firmArray?.length ? firmArray : sessionData?.firmArray || [];
  const effectiveItems = items || sessionData?.items || [];

  
  // Use the first elements from the arrays if they exist
  const orderData = effectiveOrderArray?.[0] || {};
  const firmData = effectiveFirmArray?.[0] || {};

  // Check if we need pagination (more than 20 items)
  const needsPagination = effectiveItems.length > 20;

  // Data object derived from props or session data
  const data = {
    firm: {
      GST: firmData?.GST || '',
      FirmName: firmData?.FirmName || '',
      Address1: firmData?.Address1 || '',
      Address2: firmData?.Address2 || '',
      Mobile: firmData?.MobileNo || '',
      BankName: firmData?.BankName || '',
      AccountNo: firmData?.AccountNo || '',
      IFSC: firmData?.IFSC || '',
      MsmeNo: firmData?.MsmeNo || '',
    },
    order: {
      name: orderData?.PartyName || '',
      address: orderData?.Address1 || '',
      address2: orderData?.Address2 || '',
      gst: orderData?.GSTPO ? orderData.GSTPO.replace('GST No- ', '') : '',
      orderNo: orderData?.OrderNo || '',
      orderDate: orderData?.OrderDate2 ? new Date(orderData.OrderDate2).toLocaleDateString() : '',
      deliveryDate: orderData?.DOD ? new Date(orderData.DOD).toLocaleDateString() : '',
      placeOfSupply: orderData?.PlaceOfSupply || '',
      stateCity: orderData?.StateCity || '',
    },
    // Use effective items from props or session storage
    items: effectiveItems,
    // Use order totals
    totals: {
      grandTotal: orderData?.Amount || 0,
      amountInWords: orderData?.AmountInWords || '',
    }
  };

  const tableHeaderStyle = {
    background: '#f0f0f0',
    fontWeight: 700,
    fontSize: 13,
    padding: '8px 5px',
    borderBottom: '2px solid #222',
    border: '1px solid #222',
    color: '#000',
    textAlign: 'center',
  };
  
  const tableCellRight = { textAlign: 'right', color: '#000', padding: '6px 5px', border: '1px solid #ddd', fontSize: '12px' };
  const tableCellCenter = { textAlign: 'center', color: '#000', padding: '6px 5px', border: '1px solid #ddd', fontSize: '12px' };
  const tableCellLeft = { textAlign: 'left', color: '#000', padding: '6px 5px', border: '1px solid #ddd', fontSize: '12px' };
  
  // Print-specific styles
  const printStyles = `
    @media print {
      /* Hide elements not meant for printing */
      .no-print {
        display: none !important;
      }
      
      /* Reset all margins and paddings */
      @page {
        size: A4;
        margin: 0 5mm !important;
      }
      
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        height: auto !important;
        color: black !important;
        -webkit-print-color-adjust: exact !important;
      }
      
      .page-content {
        padding: 0 !important;
        margin: 0 !important;
        background: white !important;
        color: black !important;
      }
      
      .print-container {
        border: none !important;
        box-shadow: none !important;
        padding: 7mm 10mm !important; /* Reduced from 10mm top padding to 7mm */
        margin: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        border-radius: 0 !important;
        background: white !important;
        color: black !important;
        transform: none !important;
        transform-origin: top left !important;
        box-sizing: border-box !important;
      }
      
      /* Handle pagination for the item table and post-items section */
      
      /* Make table headers repeat on each page */
      thead { 
        display: table-header-group !important;
      }
      
      /* Ensure the item rows can break across pages naturally */
      tr.item-row {
        page-break-inside: auto !important;
        line-height: 1.1 !important; /* Added line-height control */
      }
      
      /* For proper pagination of items */
      table { 
        page-break-inside: auto !important; 
        margin-top: 0 !important;  /* Remove top margin from tables */
        margin-bottom: 0 !important; /* Remove bottom margin from tables */
      }
      
      tr { 
        page-break-inside: avoid !important; 
      }
      
      /* Critical: Keep the post-items section together */
      .after-items-section {
        page-break-inside: avoid !important;
        padding-top: 5px !important; /* Reduced from 10px to 5px */
        margin-top: 8px !important; /* Reduced from 12px to 8px */
      }
      
      /* For proper spacing on the last page */
      .item-table-container {
        margin-bottom: ${needsPagination ? '0' : '3mm'} !important; /* Reduced from 5mm to 3mm */
        margin-top: 0 !important; /* Remove top margin */
      }
      
      /* Make table more compact when printing */
      table, tr, td, th {
        font-size: 11px !important;
        padding: 1px 3px !important; /* Reduced from 2px 3px to 1px 3px */
        line-height: 1.2 !important; /* Added line height control */
        border: 1px solid #222 !important; /* Ensure borders print */
      }
      
      /* Ensure table borders are visible */
      table {
        border-collapse: collapse !important;
        border: 2px solid #222 !important;
      }
      
      th {
        background-color: #f0f0f0 !important;
        font-weight: bold !important;
        text-align: center !important;
      }
      
      /* Ensure proper alignment for different columns */
      td:nth-child(1), th:nth-child(1) { /* SNo */
        text-align: center !important;
        width: 8% !important;
      }
      
      td:nth-child(2), th:nth-child(2) { /* Code */
        text-align: left !important;
        width: 10% !important;
      }
      
      td:nth-child(3), th:nth-child(3) { /* Description */
        text-align: left !important;
        width: 25% !important;
      }
      
      td:nth-child(4), th:nth-child(4) { /* Print Description */
        text-align: left !important;
        width: 18% !important;
      }
      
      td:nth-child(5), th:nth-child(5) { /* Ply */
        text-align: left !important;
        width: 15% !important;
      }
      
      td:nth-child(6), th:nth-child(6) { /* Calc */
        text-align: left !important;
        width: 12% !important;
      }
      
      td:nth-child(7), th:nth-child(7) { /* Qty */
        text-align: right !important;
        width: 10% !important;
      }
      
      td:nth-child(8), th:nth-child(8) { /* Rate */
        text-align: right !important;
        width: 10% !important;
      }
      
      td:nth-child(9), th:nth-child(9) { /* Amount */
        text-align: right !important;
        width: 10% !important;
      }
      
      /* Adjust font sizes for printing */
      h1, h2, h3, h4, h5, h6 { margin: 2px 0 !important; }
      p, div { margin: 0 !important; line-height: 1.2 !important; }
      
      /* Make the header more compact */
      .invoice-header { margin-bottom: 3px !important; } /* Reduced from 5px to 3px */
      
      /* Adjust spacing between sections */
      .section-spacing { margin-top: 3px !important; margin-bottom: 3px !important; } /* Reduced from 5px to 3px */
      
      /* Preserve spacing for amount in words section */
      .amount-words-section { margin-top: 12px !important; margin-bottom: 5px !important; } /* Reduced from 25px to 12px */
      
      /* Horizontal lines */
      hr {
        margin: 5px 0 !important; /* Reduced margin */
      }
      
      /* Ensure background colors print */
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
        color: black !important;
      }
      
      /* Keep terms and conditions together */
      .terms-section {
        page-break-inside: avoid !important;
        margin-bottom: 5px !important; /* Added smaller margin */
      }
      
      .signature-section {
        page-break-inside: avoid !important;
        margin-top: 5px !important; /* Reduced from 10px to 5px */
      }
      
      /* Ensure signature space is maintained when printing, but smaller */
      .signature-name-row {
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
      }
      
      .signature-space {
        height: 15px !important; /* Reduced from 25px to 15px */
        min-height: 15px !important;
        display: block !important;
        visibility: visible !important;
      }
      
      .signature-title-row {
        margin-top: 0 !important;
        padding-top: 0 !important;
      }
      
      /* All table cells more compact */
      td, th {
        line-height: 1.2 !important;
      }
      
      /* More compact list */
      ol, ul {
        padding-left: 15px !important; /* Reduced from 20px */
        margin: 0 !important;
      }
      
      li {
        margin-bottom: 0 !important;
        line-height: 1.2 !important;
      }
    }
  `;

  // Additional styles for screen display
  const screenTableStyles = `
    .order-table {
      border-collapse: collapse;
      width: 100%;
      border: 2px solid #222;
    }
    
    .order-table th,
    .order-table td {
      border: 1px solid #ddd;
      text-align: left;
    }
    
    .order-table th {
      background-color: #f0f0f0;
      font-weight: bold;
      text-align: center;
    }
    
    /* Column-specific alignments for screen */
    .order-table td:nth-child(1), .order-table th:nth-child(1) { text-align: center; }
    .order-table td:nth-child(6), .order-table th:nth-child(6) { text-align: right; }
    .order-table td:nth-child(7), .order-table th:nth-child(7) { text-align: right; }
    .order-table td:nth-child(8), .order-table th:nth-child(8) { text-align: right; }
  `;

  // Function to handle printing
  const handlePrint = () => {
    window.print();
  };
  
  // Helper function to safely format numbers
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '';
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '' : numValue.toFixed(2);
  };

  // Create a table header component that can be reused for each page
  const ItemTableHeader = () => (
    <thead>
      <tr>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '6px 3px', width: '8%', textAlign: 'center'}}>SNo</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '6px 3px', width: '10%', textAlign: 'center'}}>Code</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '6px 3px', width: '25%', textAlign: 'center'}}>Description</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '6px 3px', width: '15%', textAlign: 'center'}}>Ply</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '6px 3px', width: '12%', textAlign: 'center'}}>Calc</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '6px 3px', width: '10%', textAlign: 'center'}}>Qty</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '6px 3px', width: '10%', textAlign: 'center'}}>Rate</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '6px 3px', width: '10%', textAlign: 'center'}}>Amount</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '6px 3px', width: '18%', textAlign: 'center'}}>Print Description</th>
      </tr>
    </thead>
  );

  // Adjust signature space
  const signature = (
    <div className="signature-section" style={{ width: '30%', fontSize: 12, color: '#000' }}>
      <div style={{ textAlign: 'right', fontWeight: 600 }}>
        <div className="signature-name-row">For <b>{data.firm.FirmName}</b></div>
        {/* This is a spacer specifically for signatures */}
        <div className="signature-space" style={{ height: 20, border: '0px dashed transparent' }}></div>
        <div className="signature-title-row">Authorized Signatory</div>
      </div>
    </div>
  );

  return (
    <div className="page-content">
      {/* Add print-specific styles */}
      <style>{printStyles}</style>
      <style>{screenTableStyles}</style>
      
      <div
        className="print-container"
        style={{
          fontFamily: 'Segoe UI, Arial, sans-serif',
          fontSize: 13,
          maxWidth: 900,
          margin: '0 auto',
          background: '#fff',
          border: '1.5px solid #222',
          borderRadius: 8,
          boxShadow: '0 3px 15px rgba(0,0,0,0.15)',
          padding: '20px 35px', /* Reduced from 25px to 20px top/bottom padding */
          color: '#000',
          lineHeight: 1.2
        }}
      >
        {/* Header and company name section */}
        <div className="invoice-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, lineHeight: 1.2 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#000' }}>
              <span style={{ fontWeight: 600 }}>GSTIN:</span> {data.firm.GST}
            </div>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: 1, marginBottom: 2, color: '#000', textTransform: 'uppercase' }}>ORDER</div>
            <div style={{ fontWeight: 800, fontSize: 24, letterSpacing: 1, marginBottom: 2, color: '#000' }}>{data.firm.FirmName}</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#000' }}>{data.firm.Address1}</div>
            <div style={{ fontWeight: 500, fontSize: 13, color: '#000' }}>{data.firm.Address2}</div>
            <div style={{ fontWeight: 500, fontSize: 12, marginTop: 1, color: '#000' }}>{data.firm.Mobile}</div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, textAlign: 'right', color: '#000' }}>
            <div>Mobile: {data.firm.Mobile}</div>
          </div>
        </div>
        <hr style={{ border: 'none', borderTop: '2px solid #222', margin: '5px 0' }} />

        {/* Buyer/Consignee/Invoice Section - Restructured with reduced spacing */}
        <div className="section-spacing" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: '#000' }}>
          {/* BUYER - Left */}
          <div style={{ width: '30%' }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2, color: '#000' }}>BUYER</div>
            <div style={{ fontWeight: 600, color: '#000' }}>M/s : {data.order.name}</div>
            <div style={{ color: '#000', fontSize: 12 }}>{data.order.address}</div>
            <div style={{ color: '#000', fontSize: 12 }}>{data.order.address2}</div>
            <div style={{ marginTop: 2, fontWeight: 600, fontSize: 11, color: '#000' }}>
              GST No- {data.order.gst}
            </div>
          </div>
          
          {/* Consignee - Center */}
          <div style={{ width: '35%', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#000' }}>Consignee : {"  "} </div>
              <div style={{ marginLeft: 7, fontSize: 13, color: '#000' }}>{orderData?.ConsigneeAddress1 || ''}</div>
            </div>
            {orderData?.ConsigneeAddress2 && <div style={{ fontSize: 12 }}>{orderData.ConsigneeAddress2}</div>}
            {orderData?.ConsigneeAddress3 && <div style={{ fontSize: 12 }}>{orderData.ConsigneeAddress3}</div>}
          </div>
          
          {/* Invoice Details - Right */}
          <div style={{ width: '30%' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 600, padding: '2px', color: '#000' }}>Order No.</td>
                  <td style={{ padding: '2px', width: '10px', textAlign: 'center' }}>:</td>
                  <td style={{ textAlign: 'right', padding: '2px' }}>{data.order.orderNo}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '2px', color: '#000' }}>Order Date</td>
                  <td style={{ padding: '2px', textAlign: 'center' }}>:</td>
                  <td style={{ textAlign: 'right', padding: '2px' }}>{data.order.orderDate}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '2px', color: '#000' }}>Delivery Date</td>
                  <td style={{ padding: '2px', textAlign: 'center' }}>:</td>
                  <td style={{ textAlign: 'right', padding: '2px' }}>{data.order.deliveryDate}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600, padding: '2px', color: '#000' }}>Place of Supply</td>
                  <td style={{ padding: '2px', textAlign: 'center' }}>:</td>
                  <td style={{ textAlign: 'right', padding: '2px' }}>{data.order.placeOfSupply}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Item Table with reduced spacing */}
        <div className="item-table-container">
          <table className="section-spacing order-table" style={{ 
            width: '100%', 
            borderCollapse: 'collapse', 
            margin: '5px 0 0 0', 
            fontSize: 12, 
            border: '2px solid #222',
            tableLayout: 'fixed'
          }}>
            <ItemTableHeader />
            <tbody>
              {Array.isArray(data.items) && data.items.map((item, index) => {
                // Skip if item is null or undefined
                if (!item) return null;
                
                // Check if row is empty
                const isEmptyRow = !item.SNo && !item.CodeNo && !item.Description1 && 
                                  !item.Description2 && !item.Description3 && 
                                  !item.Qty && !item.Rate && !item.Amount && !item.PrintDescription;
                
                // Instead of skipping empty rows, we'll render them with no border and reduced height
                return (
                  <tr className="item-row" key={index} style={{ 
                    borderBottom: isEmptyRow ? 'none' : '1px solid #ddd',
                    backgroundColor: isEmptyRow ? 'transparent' : (index % 2 === 0 ? '#fff' : '#fafafa'),
                    height: isEmptyRow ? '20px' : 'auto',
                    lineHeight: '1.2'
                  }}>
                    <td style={{
                      ...tableCellCenter, 
                      fontSize: 12, 
                      padding: isEmptyRow ? '2px' : '4px 3px',
                      width: '8%',
                      textAlign: 'center',
                      border: isEmptyRow ? '1px solid #ddd' : undefined
                    }}>
                      {item.SNo || ''}
                    </td>
                    <td style={{
                      ...tableCellLeft, 
                      fontSize: 12, 
                      padding: isEmptyRow ? '2px' : '4px 3px',
                      width: '10%',
                      textAlign: 'left',
                      border: isEmptyRow ? '1px solid #ddd' : undefined
                    }}>
                      {item.CodeNo || ''}
                    </td>
                    <td style={{
                      ...tableCellLeft, 
                      fontSize: 12, 
                      padding: isEmptyRow ? '2px' : '4px 3px',
                      width: '25%',
                      textAlign: 'left',
                      border: isEmptyRow ? '1px solid #ddd' : undefined
                    }}>
                      {item.Description1 || ''}
                    </td>
                    <td style={{
                      ...tableCellLeft, 
                      fontSize: 12, 
                      padding: isEmptyRow ? '2px' : '4px 3px',
                      width: '15%',
                      textAlign: 'left',
                      border: isEmptyRow ? '1px solid #ddd' : undefined
                    }}>
                      {item.Description2 || ''}
                    </td>
                    <td style={{
                      ...tableCellLeft, 
                      fontSize: 12, 
                      padding: isEmptyRow ? '2px' : '4px 3px',
                      width: '12%',
                      textAlign: 'left',
                      border: isEmptyRow ? '1px solid #ddd' : undefined
                    }}>
                      {item.Description3 || ''}
                    </td>
                    <td style={{
                      ...tableCellRight, 
                      fontSize: 12, 
                      padding: isEmptyRow ? '2px' : '4px 3px',
                      width: '10%',
                      textAlign: 'right',
                      border: isEmptyRow ? '1px solid #ddd' : undefined
                    }}>
                      {item.Qty || ''}
                    </td>
                    <td style={{
                      ...tableCellRight, 
                      fontSize: 12, 
                      padding: isEmptyRow ? '2px' : '4px 3px',
                      width: '10%',
                      textAlign: 'right',
                      border: isEmptyRow ? '1px solid #ddd' : undefined
                    }}>
                      {item.Rate ? formatNumber(item.Rate) : ''}
                    </td>
                    <td style={{
                      ...tableCellRight, 
                      fontSize: 12, 
                      padding: isEmptyRow ? '2px' : '4px 3px',
                      width: '10%',
                      textAlign: 'right',
                      border: isEmptyRow ? '1px solid #ddd' : undefined
                    }}>
                      {item.Amount ? formatNumber(item.Amount) : ''}
                    </td>
                    <td style={{
                      ...tableCellLeft, 
                      fontSize: 12, 
                      padding: isEmptyRow ? '2px' : '4px 3px',
                      width: '18%',
                      textAlign: 'left',
                      border: isEmptyRow ? '1px solid #ddd' : undefined
                    }}>
                      {item.PrintDescription || ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Post-Items Section with reduced spacing */}
        <div className="after-items-section section-spacing" style={{ marginTop: 8, borderTop: '2px solid #222', paddingTop: 5 }}>
          {/* Bank Details and Order Total */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: '3px' }}>
            <tbody>
              <tr>
                <td width="75%" rowSpan="2" style={{ verticalAlign: 'top', paddingRight: '8px', borderRight: '1px solid #ddd' }}>
                  <div style={{ fontWeight: 700, marginBottom: 1, lineHeight: 1.2 }}>Our Bank:</div>
                  <div style={{ lineHeight: 1.2 }}>{data.firm.BankName}</div>
                  <div style={{ lineHeight: 1.2 }}>{data.firm.AccountNo}</div>
                  <div style={{ lineHeight: 1.2 }}>{data.firm.IFSC}</div>
                </td>
                <td width="15%" style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600, background: '#f5f5f5', color: '#000', fontSize: 11, textAlign: 'left' }}>
                  Order Total
                </td>
                <td width="10%" style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', color: '#000', fontSize: 11 }}>
                  {formatNumber(data.totals.grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
          
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, borderTop: '2px solid #222', marginTop: 3 }}>
            <tbody>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                {/* MSME moved to left side */}
                <td width="75%" style={{ textAlign: 'left', padding: '4px', color: '#000', fontSize: 11 }}>Unit Registered Under MSME Act. No.: <strong>{data.firm.MsmeNo}</strong></td>
                <td style={{ textAlign: 'right', padding: '4px', fontWeight: 700, fontSize: 13, color: '#000', whiteSpace: 'nowrap' }}>Grand Total:</td>
                <td style={{ textAlign: 'right', padding: '4px', fontWeight: 700, fontSize: 13, color: '#000', width: '25%' }}>
                  {formatNumber(data.totals.grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>

          <hr style={{ border: 'none', borderTop: '2px solid #222', margin: '8px 0 5px 0' }} />

          {/* Amount in words - Simplified for orders */}
          <div className="amount-words-section" style={{ fontSize: 11, marginBottom: 5, color: '#000', marginTop: 10, lineHeight: 1.2 }}>
            <div style={{ marginBottom: 2 }}>
              <span style={{ fontWeight: 600 }}>Amount in words: </span>
              <span style={{ fontStyle: 'italic' }}>{data.totals.amountInWords}</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            {/* Terms and Conditions section with reduced spacing */}
            <div className="terms-section" style={{ fontSize: 11, color: '#000', width: '60%', lineHeight: 1.2 }}>
              <div style={{ fontWeight: 700, marginBottom: 2, color: '#000' }}>Terms & Conditions :</div>
              <ol style={{ paddingLeft: 15, margin: 0 }}>
                <li style={{ marginBottom: 0, lineHeight: 1.2 }}>Our Firm Registered in MSME.</li>
                <li style={{ marginBottom: 0, lineHeight: 1.2 }}>Subject to Jodhpur Jurisdiction only.</li>
                <li style={{ marginBottom: 0, lineHeight: 1.2 }}>No Claim will be entertained after dispatch of Goods.</li>
                <li style={{ lineHeight: 1.2 }}>E & O.E..</li>
              </ol>
            </div>
            
            {/* Signature section with reduced spacing */}
            <div className="signature-section" style={{ width: '30%', fontSize: 12, color: '#000' }}>
              <div style={{ textAlign: 'right', fontWeight: 600, lineHeight: 1.2 }}>
                <div className="signature-name-row">For <b>{data.firm.FirmName}</b></div>
                {/* This is a spacer specifically for signatures */}
                <div className="signature-space" style={{ height: 15, border: '0px dashed transparent' }}></div>
                <div className="signature-title-row">Authorized Signatory</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Print Button - will not show when printing */}
      <div className="no-print" style={{ textAlign: 'center', marginTop: 20, marginBottom: 40 }}>
        <button
          onClick={handlePrint}
          style={{
            backgroundColor: '#2a41cc',
            color: 'white',
            border: 'none',
            borderRadius: 5,
            padding: '12px 30px',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
        >
          Print Order
        </button>
      </div>
    </div>
  );
};

// Define the prop types for validation
PrintOrderCommon.propTypes = {
  orderArray: PropTypes.arrayOf(PropTypes.shape({
    OrderNo: PropTypes.string,
    OrderDate2: PropTypes.string,
    DOD: PropTypes.string,
    PartyName: PropTypes.string,
    GSTPO: PropTypes.string,
    Address1: PropTypes.string,
    Address2: PropTypes.string,
    Amount: PropTypes.number,
    AmountInWords: PropTypes.string,
    PlaceOfSupply: PropTypes.string,
    StateCity: PropTypes.string
  })),
  firmArray: PropTypes.arrayOf(PropTypes.shape({
    GST: PropTypes.string,
    FirmName: PropTypes.string,
    Address1: PropTypes.string,
    Address2: PropTypes.string,
    MobileNo: PropTypes.string,
    BankName: PropTypes.string,
    AccountNo: PropTypes.string,
    IFSC: PropTypes.string,
    MsmeNo: PropTypes.string
  })),
  items: PropTypes.arrayOf(PropTypes.shape({
    SNo: PropTypes.number,
    Id: PropTypes.string,
    CodeNo: PropTypes.string,
    Description1: PropTypes.string,
    Description2: PropTypes.string,
    Description3: PropTypes.string,
    HSNCode: PropTypes.string,
    Qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ItemUnit: PropTypes.string,
    Amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    PrintDescription: PropTypes.string // Added PrintDescription to propTypes
  }))
};

// Define default props
PrintOrderCommon.defaultProps = {
  orderArray: [],
  firmArray: [],
  items: null
};

export default PrintOrderCommon;
